import { forwardRef, memo } from 'react'
import classNames from 'classnames'
import * as grid from '@campgladiator/cgui-core.styles.helpers.grid'
import { Heading, Paragraph } from '@campgladiator/cgui-core.atoms.typography'
import { WorkoutCard } from '../WorkoutCard'
import { WorkoutsGridProps } from '../../OnDemandWorkouts.d'
import { SkeletonWorkouts } from '../Skeleton'
import styles from './WorkoutsGrid.module.scss'

const NoResultsBanner = ({
  showNoFavoriteMsg,
}: {
  showNoFavoriteMsg: boolean
}) => (
  <div className={styles.noWorkoutsFound}>
    <Paragraph size="large" weight="book" className={styles.text}>
      {showNoFavoriteMsg
        ? 'You haven’t favorited any workout yet.'
        : 'No workouts found. Adjust your filters to find a better option.'}
    </Paragraph>
  </div>
)

const WorkoutsGrid = forwardRef<HTMLDivElement, WorkoutsGridProps>(
  (
    {
      isLoading,
      workouts,
      isLoggedIn,
      isFiltered,
      allFavorites,
      onFavoriteClick,
      isFavoriteFilterApplied,
      totalNumberOfWorkouts,
    }: WorkoutsGridProps,
    ref,
  ) => {
    const isLoadedAndFiltered =
      !isLoading && (isFiltered || isFavoriteFilterApplied)
    const noWorkoutsMatchFilters = isLoadedAndFiltered && workouts.length === 0
    const isResultsTextVisible = isLoadedAndFiltered && workouts.length > 0

    const headingText = isFavoriteFilterApplied
      ? workouts.length >= 1
        ? `${totalNumberOfWorkouts} Favorited Workouts`
        : 'Favorited Workouts'
      : `Showing ${totalNumberOfWorkouts} results based on your filters:`

    const isLoadingFavorite = (workoutId: number) =>
      (allFavorites &&
        allFavorites[workoutId] &&
        allFavorites[workoutId].isLoading) ||
      false

    return (
      <div ref={ref}>
        {isResultsTextVisible && (
          <Heading type="h5" className={styles.heading}>
            {headingText}
          </Heading>
        )}
        {noWorkoutsMatchFilters ? (
          <NoResultsBanner showNoFavoriteMsg={isFavoriteFilterApplied} />
        ) : (
          <div
            className={classNames(grid.row, styles.workouts, {
              [styles.filtered]: isFiltered,
            })}
          >
            {workouts.map((workout) => (
              <div
                className={classNames(grid.col12, grid.colMd6, grid.colLg4)}
                key={workout.id}
              >
                <WorkoutCard
                  workout={workout}
                  isLoggedIn={isLoggedIn}
                  isLoadingFavorite={isLoadingFavorite(workout.id)}
                  onFavoriteClick={(workoutId, isFavorite) =>
                    onFavoriteClick(workoutId, isFavorite)
                  }
                />
              </div>
            ))}
            {isLoading && <SkeletonWorkouts />}
          </div>
        )}
      </div>
    )
  },
)

export default memo(WorkoutsGrid)
